import type { Rectangle } from 'pixi.js';
import { TextAlignment } from '../../../../enums';
import type { TextStyleRendererSettings } from '../../TextAsset/renderer/TextStyleRenderer';
import { TextStyleRenderer } from '../../TextAsset/renderer/TextStyleRenderer';
import { TextMetrics, tallestStrings } from '../../TextAsset/metrics/TextMetrics';
import type { Transcript } from '../../../Transcript';
import { getTotalBounds } from '../../TextAsset/textMetricsHelpers';
import type { CanvasRendererSettings } from '../../TextAsset/renderer/BaseTextRenderer';
import { CanvasRenderer } from '../../TextAsset/renderer/BaseTextRenderer';
import { buildMetricsForCaptions } from '../metrics/CaptionMetrics';

export interface CaptionRendererSettings extends CanvasRendererSettings {
    transcript: Transcript;
    alignment: TextAlignment;
    normalStyle: TextStyleRendererSettings;
}

/**
 * We keep the chunk visible until the last word endTime + WORD_THRESHOLD
 */
const WORD_THRESHOLD = 1000;

/**
 * Renders a transcript. It does this by breaking the transcript into chunks of text based
 * on the provided style and dimensions (width and height). Since the transcript is time based, the active or visible
 * chunk of text is determined by the current time which is set by calling setActiveChunkAtTime().
 */
export class CaptionRenderer extends CanvasRenderer<CaptionRendererSettings> {
    #activeIndex: number = -1;
    /** The build text metrics for each chunk of text. */
    #textMetrics: TextMetrics[] = [];
    /** The build style metrics for each chunk of text. */
    #styleMetrics: TextStyleRenderer[] = [];
    /** The bounds of the text block including the graphics for each chunk. */
    #textWithGraphicsBounds: Rectangle[] = [];
    #maxBounds: Rectangle = null as unknown as Rectangle;

    transcript: Transcript = null as unknown as Transcript;
    alignment = TextAlignment.CENTER;
    style: TextStyleRenderer = new TextStyleRenderer();

    /**
     * Helper method to get the time ranges for each chunk of text.
     */
    getChunkTimeRanges() {
        if (!this.transcript) {
            return [];
        }
        return TextMetrics.getChunkTimeRanges(this.#textMetrics, this.transcript);
    }

    getChunkTexts() {
        return TextMetrics.getChunkTexts(this.#textMetrics);
    }

    /** Get the tallest text from all the chunks of text. */
    getTallestText() {
        if (!this.transcript) {
            throw new Error('no transcript');
        }
        return tallestStrings(this.#textMetrics.map((m) => m.getTallestWordElements())).join('');
    }

    get chunkCount(): number {
        return this.#textMetrics.length;
    }

    get hasChunks(): boolean {
        return this.#textMetrics.length > 0;
    }

    // @inheritdoc
    override get hasText(): boolean {
        return this.transcript.words.length > 0;
    }

    // @inheritdoc
    override get canRender(): boolean {
        // we can only render when we've built the text metrics and we seeked to an active index
        return this.#textMetrics.length > 0 && this.activeIndex !== -1;
    }

    get hasMetrics(): boolean {
        return this.#textMetrics.length !== 0;
    }

    /**
     * Set the active text metrics index based on the current time. This is used to determine which text metrics to draw.
     * We determine the active text metrics by finding the text metrics that contains the active word in the transcript.
     */
    override _onSetCurrentTime() {
        if (!this.transcript) {
            this.#activeIndex = -1;
            return;
        }
        if (this.currentTime < (this.transcript.startTime ?? 0)) {
            this.#activeIndex = -1;
            return;
        }
        const activeWordIndex = this.transcript.getActiveWordIndexAtTimeWithinThreshold(
            this.currentTime,
            WORD_THRESHOLD,
        );
        if (activeWordIndex === -1) {
            this.#activeIndex = -1;
            return;
        }
        this.#activeIndex = this.#textMetrics.findIndex((m) => {
            const [startIndex, endIndex] = m.sourceWordReferenceIndexRange;
            return startIndex! <= activeWordIndex && endIndex! >= activeWordIndex;
        });
    }

    /** The text metrics for the active chunk of text. */
    get activeText(): TextMetrics {
        if (this.#textMetrics.length === 0) {
            throw new Error('textMetrics is empty');
        }
        return this.#textMetrics[this.activeIndex];
    }

    /** The style metrics for the active chunk of text. */
    get activeStyle(): TextStyleRenderer {
        if (this.#styleMetrics.length === 0) {
            throw new Error('styles is empty');
        }
        return this.#styleMetrics[this.activeIndex];
    }

    /** The bounds of the active chunk of text. */
    get activeTextBounds(): Rectangle {
        return this.#textMetrics[this.activeIndex].bounds;
    }

    /** The index of the active chunk of text. */
    get activeIndex(): number {
        return this.#activeIndex;
    }

    get totalBounds(): Rectangle {
        return this.#maxBounds;
    }

    override apply(settings: CaptionRendererSettings) {
        super.apply(settings);
        this.transcript = settings.transcript;
        this.alignment = settings.alignment;
        this.style.apply(settings.normalStyle);
    }

    /** Build text and style metrics for each chunk of text and resize the canvas based on their total bounds. */
    build() {
        if (!this.transcript) {
            return;
        }

        this.resetBuild();

        this.#textMetrics = buildMetricsForCaptions({
            transcript: this.transcript,
            alignment: this.alignment,
            style: this.style,
            maxWidth: this.maxTextWidth,
            maxHeight: this.maxTextHeight,
        });

        if (this.#textMetrics.length === 0) {
            return;
        }

        this.#textMetrics.map((textMetrics) => {
            const bounds: Rectangle[] = [textMetrics.bounds];

            // create a clone of the style and build the graphics for the current text metrics
            const clone = this.style.clone();
            textMetrics.updateStyles(clone, clone);
            clone.graphics.forEach((graphic) => {
                graphic.build(textMetrics);
                if (!graphic.asMask) bounds.push(graphic.bounds);
            });
            this.#styleMetrics.push(clone);
            this.#textWithGraphicsBounds.push(getTotalBounds(bounds));
        });

        this.#maxBounds = getTotalBounds(this.#textWithGraphicsBounds);
    }

    /** Draw the text and style of the active chunk */
    override draw() {
        if (!this.canvas) {
            return;
        }
        if (!this.canRender) {
            this._resetCanvas();
            return;
        }
        super.draw();

        this.activeStyle.colorGraphics.reverse().forEach((graphic) => {
            graphic.draw(this.context, undefined);
        });
        this.activeText.lines.forEach((line) => {
            line.words.forEach((word) => {
                word.elements.forEach((element) => {
                    element.draw(this.context);
                });
            });
        });
    }

    override resetBuild() {
        this.#textMetrics = [];
        this.#maxBounds = null as unknown as Rectangle;
        this.#textWithGraphicsBounds = [];
        this.#activeIndex = -1;
        this.#styleMetrics = [];
    }
}
